import React, { useState, useEffect } from 'react';
import { Upload, X, Image as ImageIcon, Building2, Truck, Bus, Car, Briefcase, Info, Mail, Phone } from 'lucide-react';
import { apiClient, FleetSubtype } from '../services/api';
import { toast } from 'react-hot-toast';
import ScoringEditor from './ScoringEditor';

interface OrganisationBrandingProps {
    user: any;
}

type OrganisationType = 'fleet' | 'recruitment' | 'compliance';

interface Organisation {
    id: string;
    name: string;
    type: OrganisationType;
    subtype: FleetSubtype;
    contactEmail: string;
    contactPhone?: string;
    logoUrl?: string;
    primaryColor: string;
    secondaryColor: string;
}

/** Human-readable labels for organisation types */
const ORG_TYPE_LABELS: Record<OrganisationType, string> = {
    fleet: 'Fleet Operator',
    recruitment: 'Recruitment Agency',
    compliance: 'Compliance Service',
};

/** Human-readable labels and metadata for fleet subtypes (UI only - checks come from backend) */
const SUBTYPE_CONFIG: Record<FleetSubtype, { label: string; icon: React.ElementType; description: string }> = {
    bus: {
        label: 'Bus Operator',
        icon: Bus,
        description: 'PCV bus services with Category D requirements',
    },
    coach: {
        label: 'Coach Operator',
        icon: Bus,
        description: 'PCV coach services with Category D requirements',
    },
    haulage: {
        label: 'Haulage / HGV',
        icon: Truck,
        description: 'HGV/LGV operations with Category C requirements',
    },
    car_fleet: {
        label: 'Car Fleet',
        icon: Car,
        description: 'Company car fleet with standard Category B',
    },
    general: {
        label: 'General',
        icon: Briefcase,
        description: 'Universal compliance checks',
    },
};

const OrganisationBranding: React.FC<OrganisationBrandingProps> = ({ user }) => {
    const [organisation, setOrganisation] = useState<Organisation | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

    useEffect(() => {
        loadOrganisation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadOrganisation = async () => {
        try {
            setIsLoading(true);
            const data = await apiClient.getOrganisation();
            setOrganisation(data);
        } catch (error) {
            console.error('Error loading organisation:', error);
            toast.error('Failed to load organisation details');
        } finally {
            setIsLoading(false);
        }
    };


    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
        if (!validTypes.includes(file.type)) {
            toast.error('Please select a PNG, JPG, JPEG, or SVG file');
            return;
        }

        // Validate file size (2MB max)
        if (file.size > 2 * 1024 * 1024) {
            toast.error('File size must be less than 2MB');
            return;
        }

        setSelectedFile(file);

        // Create preview URL
        const reader = new FileReader();
        reader.onloadend = () => {
            setPreviewUrl(reader.result as string);
        };
        reader.readAsDataURL(file);
    };

    const handleUpload = async () => {
        if (!selectedFile) return;

        try {
            setIsUploading(true);

            const formData = new FormData();
            formData.append('logo', selectedFile);

            await apiClient.uploadOrganisationLogo(formData);

            toast.success('Logo uploaded successfully');

            // Reload organisation data
            await loadOrganisation();

            // Clear selection
            setSelectedFile(null);
            setPreviewUrl(null);
        } catch (error: any) {
            console.error('Error uploading logo:', error);
            toast.error(error.message || 'Failed to upload logo');
        } finally {
            setIsUploading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete your organisation logo?')) {
            return;
        }

        try {
            setIsLoading(true);

            await apiClient.deleteOrganisationLogo();

            toast.success('Logo deleted successfully');

            // Update state to remove logo
            setOrganisation(prev => prev ? { ...prev, logoUrl: undefined } : null);
        } catch (error: any) {
            console.error('Error deleting logo:', error);
            toast.error(error.message || 'Failed to delete logo');
        } finally {
            setIsLoading(false);
        }
    };

    const cancelSelection = () => {
        setSelectedFile(null);
        setPreviewUrl(null);
    };

    // Get subtype config (default to general if not found) - must be before early return
    const subtypeConfig = organisation?.subtype ? SUBTYPE_CONFIG[organisation.subtype] : SUBTYPE_CONFIG.general;
    const SubtypeIcon = subtypeConfig.icon;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin h-8 w-8 border-4 border-t-transparent rounded-full"
                     style={{ borderColor: '#69BE28 transparent transparent transparent' }}></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Page Header - Matching AutomationSettings style */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center space-x-4">
                    <div className="p-3 rounded-lg" style={{ backgroundColor: '#E8F5E9' }}>
                        <Building2 className="w-6 h-6" style={{ color: '#69BE28' }} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Organisation Settings</h1>
                        <p className="text-gray-600">View your organisation profile and customise branding settings</p>
                    </div>
                </div>
            </div>

            {/* Organisation Details Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">Organisation Details</h2>

                <div className="space-y-5">
                    {/* Organisation Name */}
                    <div className="flex items-start space-x-3">
                        <div className="p-2 bg-gray-100 rounded-lg">
                            <Building2 className="w-5 h-5 text-gray-600" />
                        </div>
                        <div className="flex-1">
                            <p className="text-sm text-gray-500">Organisation Name</p>
                            <p className="text-base font-medium text-gray-900">{organisation?.name || '-'}</p>
                        </div>
                    </div>

                    {/* Account Type */}
                    <div className="flex items-start space-x-3">
                        <div className="p-2 bg-gray-100 rounded-lg">
                            <Briefcase className="w-5 h-5 text-gray-600" />
                        </div>
                        <div className="flex-1">
                            <p className="text-sm text-gray-500">Account Type</p>
                            <p className="text-base font-medium text-gray-900">
                                {organisation?.type ? ORG_TYPE_LABELS[organisation.type] : '-'}
                            </p>
                        </div>
                    </div>

                    {/* Industry Type (Fleet orgs only) */}
                    {organisation?.type === 'fleet' && (
                        <div className="flex items-start space-x-3">
                            <div className="p-2 rounded-lg" style={{ backgroundColor: '#E8F5E9' }}>
                                <SubtypeIcon className="w-5 h-5" style={{ color: '#69BE28' }} />
                            </div>
                            <div className="flex-1">
                                <p className="text-sm text-gray-500">Industry Type</p>
                                <p className="text-base font-medium text-gray-900">{subtypeConfig.label}</p>
                                <p className="text-xs text-gray-500 mt-0.5">{subtypeConfig.description}</p>
                            </div>
                        </div>
                    )}

                    {/* Contact Email */}
                    <div className="flex items-start space-x-3">
                        <div className="p-2 bg-gray-100 rounded-lg">
                            <Mail className="w-5 h-5 text-gray-600" />
                        </div>
                        <div className="flex-1">
                            <p className="text-sm text-gray-500">Contact Email</p>
                            <p className="text-base font-medium text-gray-900">{organisation?.contactEmail || '-'}</p>
                        </div>
                    </div>

                    {/* Contact Phone */}
                    {organisation?.contactPhone && (
                        <div className="flex items-start space-x-3">
                            <div className="p-2 bg-gray-100 rounded-lg">
                                <Phone className="w-5 h-5 text-gray-600" />
                            </div>
                            <div className="flex-1">
                                <p className="text-sm text-gray-500">Contact Phone</p>
                                <p className="text-base font-medium text-gray-900">{organisation.contactPhone}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Scoring Configuration Editor - Fleet organisations only */}
            {organisation?.type === 'fleet' && (
                <ScoringEditor user={user} />
            )}

            {/* Branding Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Branding</h2>
                <p className="text-sm text-gray-600 mb-6">
                    Customise your organisation's branding by uploading a logo. Your logo will appear in the signature of all emails sent to drivers.
                </p>

                {/* Current Logo or Upload Section */}
                {organisation?.logoUrl ? (
                    <div className="flex items-start space-x-6">
                        <div className="relative w-48 h-32 border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
                            <img
                                src={`${API_URL}/${organisation.logoUrl}`}
                                alt="Organisation logo"
                                className="max-w-full max-h-full object-contain"
                            />
                        </div>
                        <div className="flex-1">
                            <p className="text-sm text-gray-600 mb-4">
                                This logo will appear in the email signature, above your organisation name.
                            </p>
                            <button
                                onClick={handleDelete}
                                disabled={isLoading}
                                className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoading ? (
                                    <>
                                        <div className="animate-spin h-4 w-4 border-2 border-red-700 border-t-transparent rounded-full mr-2"></div>
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <X className="w-4 h-4 mr-2" />
                                        Delete Logo
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                ) : (
                    /* Show upload section when no logo exists */
                    <div>
                        {/* No logo state */}
                        {!selectedFile && (
                            <div className="text-center py-6 mb-4 border-2 border-dashed border-gray-200 rounded-lg">
                                <ImageIcon className="mx-auto h-10 w-10 text-gray-400" />
                                <p className="mt-2 text-sm text-gray-600">No logo uploaded yet</p>
                                <p className="mt-1 text-xs text-gray-500">Upload a logo to display it in email signatures</p>
                            </div>
                        )}

                        <div className="space-y-4">
                            {/* File Requirements */}
                            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <Info className="h-5 w-5 text-blue-500" />
                                    </div>
                                    <div className="ml-3">
                                        <h4 className="text-sm font-medium text-blue-800">File Requirements</h4>
                                        <div className="mt-1 text-sm text-blue-700">
                                            <ul className="list-disc list-inside space-y-0.5">
                                                <li>Accepted formats: PNG, JPG, JPEG, SVG</li>
                                                <li>Maximum file size: 2MB</li>
                                                <li>Recommended: Transparent background</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* File Input or Preview */}
                            {!selectedFile ? (
                                <div>
                                    <label
                                        htmlFor="logo-upload"
                                        className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors"
                                        style={{ '--tw-ring-color': '#69BE28' } as React.CSSProperties}
                                    >
                                        <Upload className="w-4 h-4 mr-2" />
                                        Choose File
                                    </label>
                                    <input
                                        id="logo-upload"
                                        type="file"
                                        accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                                        onChange={handleFileSelect}
                                        className="hidden"
                                    />
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {/* Preview */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Preview
                                        </label>
                                        <div className="w-48 h-32 border-2 border-dashed border-gray-200 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
                                            {previewUrl && (
                                                <img
                                                    src={previewUrl}
                                                    alt="Preview"
                                                    className="max-w-full max-h-full object-contain"
                                                />
                                            )}
                                        </div>
                                    </div>

                                    {/* File Info */}
                                    <div className="text-sm text-gray-600">
                                        <p><span className="font-medium">Filename:</span> {selectedFile.name}</p>
                                        <p><span className="font-medium">Size:</span> {(selectedFile.size / 1024).toFixed(2)} KB</p>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex space-x-3">
                                        <button
                                            onClick={handleUpload}
                                            disabled={isUploading}
                                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
                                            style={{ backgroundColor: '#69BE28' }}
                                        >
                                            {isUploading ? (
                                                <>
                                                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                                    Uploading...
                                                </>
                                            ) : (
                                                <>
                                                    <Upload className="w-4 h-4 mr-2" />
                                                    Upload Logo
                                                </>
                                            )}
                                        </button>
                                        <button
                                            onClick={cancelSelection}
                                            disabled={isUploading}
                                            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OrganisationBranding;
